/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	input,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { BreakPoint, LoadingStrategy } from '@shared/data-access/enums';
import {
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	Scene7Service,
	UtilityService
} from '@shared/utils/services';
import { chain } from '@core/chain';
import { filter, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { Store as NgRxStore } from '@ngrx/store';
import { PinchToZoomComponent } from '../pinch-to-zoom/pinch-to-zoom.component';
import { selectSingleAttributeSelected } from '@pdp/attributes/data-access';
import {
	ImageFormat,
	Scene7MediaSetItem,
	Scene7MediaSetResponse,
	Scene7VideoSetItem
} from '@shared/media/data-access';
import { Environment, KillSwitches, ProcessedProduct } from '@shared/data-access/interfaces';
// eslint-disable-next-line import/no-extraneous-dependencies
import { HmfMediaSetItem, HmfProductImageData } from '@dsg-tech/homefield-utils';
import { ProductService } from '@shared/data-access/services';

@Component({
	selector: 'pdp-carousel',
	templateUrl: './carousel.component.html',
	styleUrls: ['./carousel.component.scss'],
	standalone: false
})
export class CarouselComponent implements OnChanges, OnInit, OnDestroy {
	@Input() available = true;

	@Input() catalogEntryTypeCode: string;

	@Input() eCode: string;

	@Input() productName: string;

	@Input() swatchColor = '';

	@Input() imageBadge: string;

	lazyLoadImage = input(false);

	@Input() customizedUrls: string[];

	@Input() product: ProcessedProduct;

	@Input() shouldShowOutOfStockRecs = false;

	@Input() shouldHaveBorder = false;

	@Input() alternativeInStockRecsEnabled = false;

	/**
	 * @description
	 * The kill switches
	 */
	@Input() killSwitches: KillSwitches;

	@Input() exclusiveProductType;

	@Input() mediaSetResponse: Scene7MediaSetResponse;

	private destroy$ = new Subject<void>();

	heroImage: HmfMediaSetItem;

	imageUnavailable = false;

	defaultImage =
		'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';

	currentSelectedAltImage: HmfMediaSetItem;

	altImageActiveIndex = 0;

	altImageIndexStart = 1;

	thumbnailGroupIndex = 0;

	altImageIndexEnd;

	altImageMaxDisplay = 0;

	heroImageProductImageData: Map<number, HmfProductImageData> = new Map();

	/**
	 * @description
	 * The chain identifier abbreviation
	 */
	chainIdentifierAbbr = chain.chainIdentifierAbbr;

	mediaSetItemCount = 0;

	mediaSetItems: HmfMediaSetItem[] = [];

	nearbyStoreCount = 0;
	doesCurrentlySetStoreHaveInventory: boolean;

	private fullScreenLightGalleryInstance: any;

	private mediaSetSuffix = '';

	private swatchColorPrefix = '';

	selectedAttributeSubscription$ = this.ngrxStore.select(selectSingleAttributeSelected);

	/**
	 * @description
	 * Max number of alt images shown on carousel at a time.
	 */
	numCarouselImageDisplayed = 5;

	/**
	 * @description
	 * Start index for desktop carousel.
	 */
	carouselStart = 1;

	imageLoading = false;

	indexOfMedia: number;

	clickTargetOfMedia: string;

	syndigoHotspotsEnabled = false;

	isPlatformBrowser = false;

	isPLA = false;

	/**
	 * @description
	 * End index for desktop carousel.
	 */
	carouselEnd = this.numCarouselImageDisplayed;

	@ViewChild('pinch') pdpPinchToZoom: PinchToZoomComponent;

	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly environment: Environment,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly deviceService: DeviceService,
		private readonly eventBusService: EventBusService,
		private readonly hostElement: ElementRef,
		@Inject(PLATFORM_ID) protected platformId: Object,
		private readonly scene7Service: Scene7Service,
		private readonly utilityService: UtilityService,
		private readonly ngrxStore: NgRxStore,
		private readonly productService: ProductService
	) {}

	ngOnInit(): void {
		this.isPlatformBrowser = isPlatformBrowser(this.platformId);
		this.isPLA = this.productService.isPLA(this.isPlatformBrowser);
		this.imageLoading = true;

		if (this.customizedUrls) {
			this.setCustomizedUrls();
			return;
		}

		this.listenForAttributeSwatchColorChangesFromState();

		this.syndigoHotspotsEnabled = this.utilityService.checkKillSwitch(
			'syndigoHotspotsEnabled',
			this.killSwitches,
			this.chainIdentifierAbbr
		);

		// Set the hero image as the default product image while waiting for the media set
		this.setHeroImage();

		// Check the swatch color
		if (typeof this.swatchColor === 'string' && this.swatchColor.trim() !== '') {
			// eslint-disable-next-line no-useless-escape
			this.swatchColor = this.swatchColor.replace(/[\/ -]/g, '_');
			this.swatchColorPrefix = '_';
		}

		// Check the type code to see if the suffix needs updated
		if (
			typeof this.catalogEntryTypeCode === 'string' &&
			this.catalogEntryTypeCode.toLowerCase() !== 'bundlebean'
		) {
			this.mediaSetSuffix = '_is';
		}

		this.getMediaSetScene7();

		if (this.exclusiveProductType) {
			this.imageBadge = this.exclusiveProductType;
		}
	}

	/**
	 * @description
	 * Angular lifecycle event
	 * NOTE: To test in dev mode manually, use this in console after selecting in-stock-component in the Elements panel:
	 * var inStockComp = ng.getComponent($0); inStockComp.selectedColorAttribute = 'Clay'; ng.markDirty(inStockComp);
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (this.customizedUrls) {
			this.setCustomizedUrls();
			return;
		}
		// Check to see if the swatch color changed
		if (
			typeof changes !== 'undefined' &&
			typeof changes.swatchColor !== 'undefined' &&
			changes.swatchColor.currentValue !== changes.swatchColor.previousValue &&
			!changes.swatchColor.firstChange
		) {
			// Check the execution context
			this.setProductImageData();

			// Set the hero image as the default product image while waiting for the media set
			this.setHeroImage();

			// reset indexs
			this.carouselStart = 1;
			this.carouselEnd = this.numCarouselImageDisplayed;

			// Set the active index to 0
			this.altImageActiveIndex = 0;

			// Reset position on mobile gallery when the color selection changes
			const gallery = this.document.getElementsByClassName('gallery no-gutters');

			if (gallery?.length) {
				gallery[0].scrollLeft = 0;
			}

			// Clear the lightGallery instance
			this.fullScreenLightGalleryInstance = undefined;
		}

		if (changes.imageBadge && this.imageBadge) {
			this.imageBadge = this.utilityService.removeWhiteSpace(this.imageBadge);
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	setProductImageData() {
		if (isPlatformBrowser(this.platformId)) {
			// Get the image set
			this.scene7Service
				.getProductImageSet$(
					this.eCode,
					this.swatchColor,
					this.swatchColorPrefix ? this.swatchColorPrefix : '_',
					this.mediaSetSuffix ? this.mediaSetSuffix : '_is',
					0
				)
				.subscribe({
					next: (mediaSetResponse: Scene7MediaSetResponse) => {
						// Check to ensure there is a media set
						if (!mediaSetResponse?.item) {
							this.setDefaultHeroOnImageUnavailable();
						} else {
							// Parse the Scene7 MediaSet items
							const newMediaSetItems = this.parseMediaSetItems(
								Array.isArray(mediaSetResponse.item)
									? mediaSetResponse.item
									: [mediaSetResponse.item]
							);

							// Clear for fresh set of image data, if we don't clear, clicking on a new color attribute
							// can mess up order of new image gallery for that new color, so clear on fresh grab of media images.
							//See: https://dcsgcloud.atlassian.net/browse/PDSL-557
							this.mediaSetItems = [];
							// Run change detection to set the new values
							this.changeDetectorRef.detectChanges();
							// Update the hero image with the first image in the media set
							this.heroImage = newMediaSetItems[0];
							this.mediaSetItems = [...newMediaSetItems];

							this.mediaSetItems.unshift(this.heroImage);

							// Set the media set count, subtract 1 to offset the duplicate item added above
							this.mediaSetItemCount = this.mediaSetItems.length - 1;
							// Set the alt image index end
							this.altImageIndexEnd =
								this.altImageMaxDisplay + this.altImageIndexStart;

							// Ensure Product Image Unavailable does not show.
							this.imageUnavailable = false;
						}
					},
					error: () => {
						this.setDefaultHeroOnImageUnavailable();
					}
				});
		}
	}

	/**
	 * @description
	 * Set the hero image as the default product image while waiting for the media set
	 */
	setHeroImage() {
		this.heroImage = {
			src: this.scene7Service.getProductImageUrl(
				this.eCode,
				this.catalogEntryTypeCode,
				this.swatchColor,
				undefined,
				{ wid: '1400', fmt: 'pjpeg' },
				false
			),
			templateSrc: this.scene7Service.getProductImageUrl(
				this.eCode,
				this.catalogEntryTypeCode,
				this.swatchColor,
				undefined,
				{},
				false
			),
			type: 'image',
			index: 0
		};
	}

	/**
	 * @description
	 * Click listener for alt images so that the hero image gets updated to the currently selected alt
	 */
	onAltImageClick(index: number) {
		// Ensure there is a valid entry
		if (Array.isArray(this.mediaSetItems) && this.mediaSetItems.length - 1 >= index) {
			// Update the hero image with the alt that was selected
			this.heroImage = this.mediaSetItems[index + 1];

			this.mediaSetItems[0] = this.heroImage;
			// Update currently selected alt image
			this.currentSelectedAltImage = this.heroImage;
			// Update the selected alt index
			this.altImageActiveIndex = index;
		}
	}

	listenForAttributeSwatchColorChangesFromState(): void {
		this.selectedAttributeSubscription$
			.pipe(
				filter((x) => x != null),
				takeUntil(this.destroy$)
			)
			.subscribe((attributeSelected) => {
				if (attributeSelected && attributeSelected.name.toLowerCase() === 'color') {
					// eslint-disable-next-line no-useless-escape
					this.swatchColor = attributeSelected.value.replace(/[\/ -]/g, '_');
					// Set the active index to 0
					this.carouselStart = 1;
					this.carouselEnd = this.numCarouselImageDisplayed;

					// Set the active index to 0
					this.altImageActiveIndex = 0;

					// Set the media set count
					this.mediaSetItemCount = 0;

					// Clear the lightGallery instance
					this.fullScreenLightGalleryInstance = undefined;

					this.setHeroImage();

					if (this.mediaSetItems) {
						this.mediaSetItems.unshift(this.heroImage);
					}
					this.setProductImageData();
				}
			});
	}

	// click event for Hero Image
	loadContent(index: number, clickTarget: string, mediaSetType: string) {
		this.indexOfMedia = index;
		this.clickTargetOfMedia = clickTarget;
		// Load the lightGallery stylesheets
		this.loadLightGalleryStyles();
		//open Full Screen Target
		if (this.imageLoading && mediaSetType === 'video') {
			window.requestAnimationFrame(() => this.onFullScreenTargetClick());
			this.imageLoading = false;
		} else {
			this.onFullScreenTargetClick();
		}
	}

	/**
	 * @description
	 * Click listener for targets which initialize full screen mode
	 */
	onFullScreenTargetClick() {
		// Get a reference to the native image element
		const nativeImageElement = this.hostElement.nativeElement.querySelector(
			`#image-viewer-hero-wrapper-${this.eCode} img`
		);

		// Ensure there is an image
		if (
			typeof nativeImageElement !== 'undefined' &&
			nativeImageElement !== null &&
			typeof this.fullScreenLightGalleryInstance === 'undefined'
		) {
			let lgOptions = {
				autoplay: false,
				autoplayControls: false,
				hash: false,
				share: false,
				speed: 300,
				swipeThreshold: 100,
				mousewheel: false,
				getCaptionFromTitleOrAlt: false,
				download: false,
				autoplayFirstVideo: false,
				actualSize: false,
				closable: false,
				index: this.indexOfMedia || 0,
				loop: true,
				dynamic: true,
				dynamicEl: this.mediaSetItems.slice(1, this.mediaSetItems.length),
				hideBarsDelay: 600000
			};

			// Check the viewport width
			if (this.deviceService.getDeviceBreakPoint() > BreakPoint.SMALL) {
				// This is expensive! Only calling directly and forcing recalculate because it is on a click event and not page load or window resize!
				const viewportDimensions = this.deviceService.getDeviceViewportDimensions(true);

				lgOptions = Object.assign(
					{
						thumbnail: true,
						thumbWidth: viewportDimensions.width / 6,
						thumbContHeight: viewportDimensions.height / 5,
						thumbMargin: 10
					},
					lgOptions
				);
			}

			(($) => {
				// Create the lightGallery Class instance
				this.fullScreenLightGalleryInstance = $(nativeImageElement).lightGallery(lgOptions);

				// Register the close Event
				this.fullScreenLightGalleryInstance.on('onCloseAfter.lg', () => {
					// Un-register the close Event
					this.fullScreenLightGalleryInstance.off('onCloseAfter.lg');

					// Un-register the after slide Event
					this.fullScreenLightGalleryInstance.off('onAfterSlide.lg');

					// Destroy the lightGallery jQuery instance
					this.fullScreenLightGalleryInstance.data('lightGallery').destroy(true);

					// Reset the lightGallery Class reference
					this.fullScreenLightGalleryInstance = undefined;
				});
			})(jQuery);
		}
	}

	/**
	 * @description
	 * Loads the lightGallery plugin stylesheets
	 */
	private loadLightGalleryStyles(): void {
		// Load the main lightGallery stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lightgallery.min.css',
			'lg-main'
		);

		// Load the lightGallery transitions stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lg-transitions.min.css',
			'lg-transitions'
		);
	}

	toggleStoreLayerSideNav() {
		this.eventBusService.emit(new EmitEvent(Events.ChangeStore));
	}

	/**
	 * @description
	 * get media set items using scene 7
	 */
	private getMediaSetScene7() {
		// Get the image set
		const getProductImageSet$ =
			this.mediaSetResponse !== undefined
				? of(this.mediaSetResponse)
				: this.scene7Service.getProductImageSet$(
						this.eCode,
						this.swatchColor,
						this.swatchColorPrefix,
						this.mediaSetSuffix,
						0
					);

		getProductImageSet$.subscribe({
			next: (mediaSetResponse: Scene7MediaSetResponse) => {
				// Check to ensure there is a media set
				if (typeof mediaSetResponse?.item !== 'undefined') {
					// Parse the Scene7 MediaSet items
					this.mediaSetItems = this.parseMediaSetItems(
						Array.isArray(mediaSetResponse.item)
							? mediaSetResponse.item
							: [mediaSetResponse.item]
					);

					// debug media set items
					// Update the hero image with the first image in the media set
					this.heroImage = this.mediaSetItems[0];

					this.mediaSetItems.unshift(this.heroImage);

					// Set the media set count, subtract 1 to offset the duplicate item added above
					this.mediaSetItemCount = this.mediaSetItems.length - 1;

					// Set the alt image index end
					this.altImageIndexEnd = this.altImageMaxDisplay + this.altImageIndexStart;
				} else {
					this.setDefaultHeroOnImageUnavailable();
				}
			},
			error: () => {
				this.setDefaultHeroOnImageUnavailable();
			}
		});
	}

	buildHmfProductImageData(mediaSetItem: HmfMediaSetItem, index: number): HmfProductImageData {
		return {
			defaultColor: this.swatchColor,
			height: '300',
			imageUrl: mediaSetItem.src,
			imgClasses: 'hmf-px-m gallery-image',
			imgFormat: ImageFormat.PJPEG,
			isLCPImage: index === 0,
			isNgOptimizedImageSupported: index === 0,
			sourceImg: mediaSetItem.src,
			width: '300'
		};
	}

	setImageAsPriority(index: number): boolean {
		return !this.lazyLoadImage() && index === 0 && !this.isPLA;
	}

	setLoadingStrategy(index: number): string {
		return index === 0 ? LoadingStrategy.Eager : LoadingStrategy.Lazy;
	}

	/**
	 * @description
	 * Parsed a given Scene7 MediaSet item array and builds an object with properties to be used in the template
	 */
	private parseMediaSetItems(scene7MediaSetItems: Scene7MediaSetItem[]): HmfMediaSetItem[] {
		// Return data
		const returnMediaSetItems: HmfMediaSetItem[] = [];

		// Clear the hero image product image data
		this.heroImageProductImageData.clear();

		// Image widths for the srcset
		const FULLSCREEN_IMAGE_WIDTHS = [685, 1000, 1500, 2000];

		// Early exit if there are no items to iterate through
		if (Array.isArray(scene7MediaSetItems) && scene7MediaSetItems.length === 0) {
			return returnMediaSetItems;
		}

		// Iterate through the Scene7 MediaSet items
		for (let i = 0, length = scene7MediaSetItems.length; i < length; i += 1) {
			// Get a reference to the current index for convenience and to prevent multiple lookups
			const currentScene7MediaSetItem: Scene7MediaSetItem = scene7MediaSetItems[i];

			// The image file name in Scene7
			let scene7ImageName: string;

			// Check where to get the image name from.  ImageComingSoon will not have a swatch object and only an image object
			if (typeof currentScene7MediaSetItem.s !== 'undefined') {
				// Use the swatch object
				scene7ImageName = currentScene7MediaSetItem.s.n;
			} else {
				// use the image object
				scene7ImageName = currentScene7MediaSetItem.i.n;
			}

			// Build a new parsed media set item
			const mediaSetItem: HmfMediaSetItem = this.buildMediaSetItem(
				i,
				scene7ImageName,
				currentScene7MediaSetItem,
				FULLSCREEN_IMAGE_WIDTHS
			);

			// Add the media set item to the return array
			returnMediaSetItems.push(mediaSetItem);
		}

		// Return the data
		return returnMediaSetItems;
	}

	private buildMediaSetItem(
		i: number,
		scene7ImageName: string,
		currentScene7MediaSetItem: Scene7MediaSetItem,
		FULLSCREEN_IMAGE_WIDTHS: number[]
	) {
		const mediaSetItem: HmfMediaSetItem = {
			srcset: '',
			type: '',
			index: i
		};

		// Set the thumbnail and src properties
		mediaSetItem.thumb = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=250&hei=250&fmt=pjpeg`;
		mediaSetItem.templateSrc = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}`;

		// Determine which type of asset the current item is
		if (
			typeof currentScene7MediaSetItem.type === 'string' &&
			currentScene7MediaSetItem.type.toLowerCase() === 'video'
		) {
			// Set the poster
			mediaSetItem.poster = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

			// Set the type
			mediaSetItem.type = 'video';

			// Set the video HTML
			// Set the track source to use the first video name
			const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.closedCaption}${currentScene7MediaSetItem.i.n}_CC.vtt">`;

			mediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false">
                                            <source src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.videoDirectory}${currentScene7MediaSetItem.i.n}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;
		} else if (
			typeof currentScene7MediaSetItem.type === 'string' &&
			currentScene7MediaSetItem.type.toLowerCase() === 'video_set'
		) {
			// Set the poster
			mediaSetItem.poster = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

			// Handle case where there is only 1 video item
			const videoItems = Array.isArray(currentScene7MediaSetItem.set.item)
				? currentScene7MediaSetItem.set.item
				: [currentScene7MediaSetItem.set.item];

			// Iterate through the VideoDesc items
			const videos = videoItems.map(
				(videoDesc: Scene7VideoSetItem) =>
					`${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.videoDirectory}${videoDesc.i.n}`
			);

			// Set the type
			mediaSetItem.type = 'video';

			// Set the video HTML to the first video in the array
			// Set the track source to use the first video name
			const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.closedCaption}${videoItems[0].i.n}_CC.vtt">`;

			mediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false"><source src="${videos[0]}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;
		} else {
			/*
			 * Default to image / image set because they do not have type defined on the JSONP response
			 */
			// Iterate through the image sizes
			FULLSCREEN_IMAGE_WIDTHS.forEach((width) => {
				// Build the image URL
				const image = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=${width}&hei=${width}&fit=constrain&fmt=pjpeg`;

				// Add to the element srcset
				mediaSetItem.srcset += `${image} ${width}w, `;
			});

			// Set the type
			mediaSetItem.type = 'image';

			// Set the src
			mediaSetItem.src = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&hei=1400&fit=constrain&fmt=pjpeg`;

			this.heroImageProductImageData.set(i, this.buildHmfProductImageData(mediaSetItem, i));
		}
		return mediaSetItem;
	}

	setDefaultHeroOnImageUnavailable(): void {
		this.mediaSetItems = [];
		this.heroImage = {
			poster: this.defaultImage,
			templateSrc: this.defaultImage
		};
		this.imageUnavailable = true;
	}

	private setCustomizedUrls() {
		this.mediaSetItems = [];
		for (let i = 0; i < this.customizedUrls.length; ++i) {
			this.mediaSetItems.push({
				src: this.customizedUrls[0],
				templateSrc: this.customizedUrls[0],
				thumb: this.customizedUrls[0],
				type: 'image',
				index: i
			});
		}

		this.heroImage = {
			src: this.customizedUrls[0],
			templateSrc: this.customizedUrls[0],
			type: 'image',
			index: 0
		};

		this.mediaSetItems.unshift(this.heroImage);

		// Set the media set count, subtract 1 to offset the duplicate item added above
		this.mediaSetItemCount = this.mediaSetItems.length - 1;
	}

	/**
	 * @description
	 * This handles the swipe on the gallery component as scroll event and aligns the image indicators with the images in mobile view.
	 */
	handleGalleryScroll(event: Event) {
		const galleryItemElements = document.querySelectorAll<HTMLElement>('.gallery-item');
		const galleryItemsPositions = [];
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		galleryItemElements.forEach((item, idx) => {
			galleryItemsPositions.push(item.offsetLeft);
		});
		const currentElementPosition = (event.target as HTMLElement).scrollLeft;
		galleryItemsPositions.forEach((itemLeft, idx) => {
			if (itemLeft <= currentElementPosition) {
				this.altImageActiveIndex = idx;
				this.heroImage = this.mediaSetItems[this.altImageActiveIndex + 1];
				this.mediaSetItems[0] = this.heroImage;
				this.currentSelectedAltImage = this.heroImage;
			}
		});
	}

	// If an individual image in the media set errors, replace that image with the Image Unavailable image
	handleImageError(imageIndex: number) {
		// just templateSrc is used in carousel, everything else is needed for lightgallery
		this.mediaSetItems[imageIndex].src = this.defaultImage;
		this.mediaSetItems[imageIndex].srcset = this.defaultImage;
		this.mediaSetItems[imageIndex].templateSrc = this.defaultImage;
		this.mediaSetItems[imageIndex].thumb = this.defaultImage;
	}
}
